"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useEffect, useId, useRef, useState } from "react";
import { Controller, type FieldErrors, type SubmitHandler, useForm } from "react-hook-form";

import type {
	DialogProps,
	SnackbarCloseReason,
	SnackbarOrigin
} from "@/components/controls/base-controls";
import {
	Box,
	Button,
	IconButton,
	MuiTelInput,
	Snackbar,
	SnackbarContent,
	Stack,
	TextField,
	Typography,
    useMediaQuery,
    useTheme
} from "@/components/controls/base-controls";
import {
	ArrowDropDownIcon,
	ArrowDropUpIcon,
	ArrowRightAltIcon,
	CloseIcon,
	CompareArrowsIcon,
	DateRangeIcon,
	LocalAirportIcon,
	PersonIcon,
	SearchIcon,
} from "@/components/controls/icons";
import AirportsDialog from "@/components/dialogs/airports.dialog";
import CalendarPopover from "@/components/popovers/calendar.popover";
import FlightTypePopover from "@/components/popovers/flight-type.popover";
import PassengerPopover from "@/components/popovers/passenger.popover";
import ConditionalTooltip from "@/components/tooltips/conditional.tooltip";

import { unwrapDal, DalMutationError } from "@/core/dal/unwrap";
import {
	type FlightFormData,
	flightFormSchema,
	type TripFormPayload,
} from "@/core/schemas/flight.form.schema";
import ServiceRegistry from "@/core/services/service-registry";
import TripsService from "@/core/services/trips.service";
import type { Airport } from "@/core/types/airport.type";
import { Direction } from "@/core/types/direction.type";
import { Flight, type FlightValue } from "@/core/types/flight.type";
import { useLoadingStore } from "@/core/utils/loading.store";

const formatDate = (iso: string, timeZone?: string) => new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timeZone,
    timeZoneName: 'short',
}).format(new Date(iso));

interface SnackbarState extends SnackbarOrigin {
    open: boolean;
    message?: string;
    severity?: "success" | "error";
};

const FlightReqForm = () => {
    const t = useTranslations();

    const { isLoading } = useLoadingStore();

    const theme = useTheme();
    // Returns true on mobile (xs, sm), false on desktop (md, lg, xl)
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [openSnackbar, setOpenSnackbar] = useState<SnackbarState>({
        open: false,
        vertical: "top",
        horizontal: "center",
    });

    const { vertical, horizontal, open, severity, message } = openSnackbar;

    const email_id = useId();
    const given_name_id = useId();
    const family_name_id = useId();
    const phone_number_id = useId();

    // Dialog UI States
    const [direction, setDirection] = useState<Direction>(Direction.FROM);
    const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogScroll, setDialogScroll] = useState<DialogProps["scroll"]>("paper");

    // Popover Anchor DOM Elements
    const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [passengerAnchorEl, setPassengerAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [flightAnchorEl, setFlightAnchorEl] = useState<HTMLButtonElement | null>(null);

    // 1. 🔑 Create the DOM element pointer inside your main form component state section
    const toAirportButtonRef = useRef<HTMLButtonElement | null>(null);

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors },
    } = useForm<FlightFormData>({
        mode: "onSubmit",
        resolver: zodResolver(flightFormSchema),
        defaultValues: {
            email: "",
            given_name: "",
            phone_number: "",
            submitted_at: "",
            family_name: "",
            segments: [
                {
                    type: "flight",
                    flight_type: Flight.ROUNDTRIP,
                    departure_airport: undefined,
                    arrival_airport: undefined,
                    departure_date: undefined,
                    return_date: undefined,
                    passenger_count: 1,
                },
            ],
        },
    });

    // 2. Establish live data pipelines from the segments array
    const segments = watch("segments");
    const flightSegment = segments?.[0]?.type === "flight" ? segments[0] : null;

    const departure_airport = flightSegment?.departure_airport;
    const arrival_airport = flightSegment?.arrival_airport;
    const flight_type = flightSegment?.flight_type ?? Flight.ROUNDTRIP;
    const passenger_count = flightSegment?.passenger_count ?? 1;
    
    const dateRange = [
        flightSegment?.departure_date, 
        flightSegment?.return_date
    ].filter((d): d is Date => d instanceof Date);

    const openCalendarPopover = Boolean(calendarAnchorEl);
    const openPassengerPopover = Boolean(passengerAnchorEl);
    const openFlightTypePopover = Boolean(flightAnchorEl);
    // 3. Dynamic layout focus routing rule
    useEffect(() => {
        if (departure_airport && !arrival_airport) {
        toAirportButtonRef.current?.focus();
        }
    }, [departure_airport, arrival_airport]);

    // Dialogue state transitions
    const handleDialogClose = (newAirport: Airport | undefined, shouldClose: boolean = true) => {
        if (shouldClose) {
            setDialogOpen(false); // Only close if an option was clicked or "X" header button was pressed
        }
        
        if (direction === Direction.FROM) {
            setValue("segments.0.departure_airport", newAirport as Airport, { shouldValidate: true });
        } else {
            setValue("segments.0.arrival_airport", newAirport as Airport, { shouldValidate: true });
        }
    };

    const handleDialogOpen = (dir: Direction, scrollType: DialogProps["scroll"]) => () => {
        setDirection(dir);
        setDialogOpen(true);
        setDialogScroll(scrollType);
    };

    // Popover handlers mutating straight into the React Hook Form data model
    const handleDateBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => setCalendarAnchorEl(e.currentTarget);
    const handleCalendarPopoverClose = (val: Date[]) => {
        setCalendarAnchorEl(null);
        const [dDate, rDate] = val;
        if (dDate) {
            setValue("segments.0.departure_date", dDate, { shouldValidate: true });
            setValue("segments.0.return_date", rDate, { shouldValidate: true });
        }
    };

    const handlePassengerBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => setPassengerAnchorEl(e.currentTarget);
    const handlePassengerPopoverClose = (val: number) => {
        setPassengerAnchorEl(null);
        setValue("segments.0.passenger_count", val, { shouldValidate: true });
    };

    const handleFlightBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => setFlightAnchorEl(e.currentTarget);
    const handleFlightTypePopoverClose = (val: FlightValue | undefined) => {
        setFlightAnchorEl(null);
        if (val && val !== Flight.MULTICITY) {
            if (val === Flight.ONEWAY) {
                setValue("segments.0.flight_type", val, { shouldValidate: true });
            }
            setValue("segments.0.flight_type", val, { shouldValidate: true });
        }
    };

    const onSubmit: SubmitHandler<FlightFormData> = async (data) => {
        try {
            const tripsService = ServiceRegistry.get(TripsService);

            const { segments, ...rest } = data;
            const [first_segment, ...other_segments] = segments;
            const { departure_airport, arrival_airport, ...segmentRest } = first_segment as typeof first_segment & { departure_airport: Airport; arrival_airport: Airport };

            const payload = {
                ...rest,
                segments: [
                    {
                        ...segmentRest,
                        departure_airport,
                        arrival_airport,
                    },
                    ...other_segments,
                ],
                submitted_at: formatDate(new Date().toISOString(), Intl.DateTimeFormat().resolvedOptions().timeZone)
            };

            // Unwrap the DalReturn: resolves on success, throws DalMutationError on failure
            unwrapDal(await tripsService.submitRequest(payload as TripFormPayload));

            setOpenSnackbar((prev) => ({ ...prev, open: true, severity: "success", message: t("flightReq.submission.success") }));

            // Reset form variables smoothly back to clear default values
            reset();

        } catch (error) {
            console.error("Flight submission failed:", error);
            // Surface the DAL error via its i18n key, falling back to the generic failure message
            const message = error instanceof DalMutationError
                ? t(error.i18nKey)
                : t("flightReq.submission.failure");
            setOpenSnackbar((prev) => ({ ...prev, open: true, severity: "error", message }));
        }
    };

    const handleSnackbarClose = (
            _event: React.SyntheticEvent | Event,
            reason?: SnackbarCloseReason,
        ) => {
            if (reason === "clickaway") {
                return; // Prevent closing on clickaway
            }
            setOpenSnackbar({ ...openSnackbar, open: false }); // Close only when explicitly triggered or other reasons
        };

    // 🔑 Safely extract errors for the first segment of the trip
    const segmentErrors = (Array.isArray(errors.segments) ? errors.segments[0] : undefined) as FieldErrors<{ departure_airport: Airport; arrival_airport: Airport; return_date: Date }> | undefined;

    return (
        <Box sx={{ flex: 1 }}>

            <Box
                component="form" 
                noValidate 
                aria-busy={isLoading} aria-describedby="data-processing"
                onSubmit={handleSubmit(onSubmit)}>

                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: {xs: "center", md: "flex-start"},
                    gap: 2,
                    width: "600px",
                    maxWidth: "90vw",
                    margin: "0 auto"
                }}
                >
                    {/* ROW 1: FLIGHT TYPE, DATES AND PASSENGER COUNT */}
                    <Stack direction={{ xs: "column", md: "row" }}  sx={{ 
                            alignItems: {xs: "center", md: "flex-start"}, justifyContent: "flex-start", 
                            gap: {xs: 2, md: 1}, width: { xs: "100%", md: "100%" } 
                        }}>
                        
                        {/* BUTTON: TRIP SPECIFICATION CONFIGURATOR */}
                        <Button type="button" variant="outlined" 
                            sx={{ minHeight: "4em", width: {xs: "100%", md: "33%"}, textTransform: "none", justifyContent: { xs: "space-between", md: "center" } }} startIcon={flight_type === Flight.ROUNDTRIP ? <CompareArrowsIcon /> : <ArrowRightAltIcon />} endIcon={openFlightTypePopover ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />} onClick={handleFlightBtnClick}> 
                            <Typography sx={{ py: ".3em", textAlign: "left", flexGrow: 1 }}> 
                                {t(`flightReq.${flight_type.toString()}`)} 
                            </Typography> 
                        </Button>

                        {/* BUTTON: CALENDAR TICKETING TIMELINE SELECTOR */}
                        <Button type="button" variant="outlined" 
                            sx={{ minHeight: "4em", width: {xs: "100%", md: "34%"}, textTransform: "none", justifyContent: { xs: "space-between", md: "center" } }} startIcon={<DateRangeIcon />} endIcon={openCalendarPopover ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />} onClick={handleDateBtnClick}> 
                            <Typography sx={{ p: ".4em 0 .2em 0", textAlign: "left", flexGrow: 1 }}> 
                                {dateRange[0] 
                                    ? dateRange[0].toLocaleString('en-US', { month: 'short', day: 'numeric'}) 
                                    : t("flightReq.depart")} 
                                {dateRange[1] 
                                    ? ` - ${dateRange[1].toLocaleString('en-US', { month: 'short', day: 'numeric' })}` 
                                    : (flight_type === Flight.ROUNDTRIP) && ` - ${t("flightReq.return")}`} 
                            </Typography> 
                        </Button> 

                        <Button type="button" variant="outlined" 
                            sx={{ minHeight: "4em", width: {xs: "100%", md: "33%"}, textTransform: "none", justifyContent: { xs: "space-between", md: "center" } }}
                            startIcon = {<PersonIcon />}
                            endIcon={ openPassengerPopover ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                            onClick={handlePassengerBtnClick}
                        >
                            <Typography sx={{ py: ".3em", textAlign: "left", flexGrow: 1 }}>
                                {t("flightReq.passengers", { count: passenger_count })}
                            </Typography>
                            
                        </Button>

                    </Stack>

                    {/* ROW 2: AIRPORT HUB SELECTION ELEMENT PANELS */}
                    <Stack direction={{ xs: "column", md: "row" }}  sx={{
                            alignItems: {xs: "center", md: "flex-start"}, justifyContent: "flex-start", 
                            gap: {xs: 2, md: 1}, width: { xs: "100%", md: "100%" } 
                        }}>
                        
                        {/* BUTTON: DEPARTURE ("FROM") */}
                        <Button type="button" variant="outlined" sx={{ minHeight: "4em", textTransform: "none", justifyContent: "center", flex: 1, minWidth: 0, width: "100%" }} startIcon={<LocalAirportIcon />} endIcon={<SearchIcon />} onClick={handleDialogOpen(Direction.FROM, "paper")}> 
                            <ConditionalTooltip title={departure_airport ? departure_airport.name : t("airport.notSelected")}> 
                                <Typography sx={{ py: ".3em", textAlign: "left", flexGrow: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}> 
                                    {departure_airport ? `${departure_airport.code} - ${departure_airport.name}` : t(`flightReq.${Direction.FROM}`)} 
                                </Typography> 
                            </ConditionalTooltip> 
                        </Button>

                        {/* BUTTON: ARRIVAL ("TO") */}
                        <Button ref={toAirportButtonRef} type="button" variant="outlined" sx={{ minHeight: "4em", textTransform: "none", justifyContent: "center", flex: 1, minWidth: 0, width: "100%" }} startIcon={<LocalAirportIcon />} endIcon={<SearchIcon />} onClick={handleDialogOpen(Direction.TO, "paper")}> 
                            <ConditionalTooltip title={arrival_airport ? arrival_airport.name : t("airport.notSelected")}> 
                                <Typography sx={{ py: ".3em", textAlign: "left", flexGrow: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}> 
                                    {arrival_airport ? `${arrival_airport.code} - ${arrival_airport.name}` : t(`flightReq.${Direction.TO}`)} 
                                </Typography> 
                            </ConditionalTooltip> 
                        </Button>
                    </Stack>

                    <Stack direction={{ xs: "column", md: "row" }}  sx={{
                        alignItems: {xs: "center", md: "flex-start"}, justifyContent: "flex-start", 
                        rowGap: 2, columnGap: 1, width: "100%" 
                    }}>

                        <Controller
                            name="given_name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    required
                                    id={given_name_id}
                                    type="text"                                
                                    label={t("name.given_name.label")}
                                    error={!!errors.given_name}
                                    helperText={
                                        errors?.given_name?.message && t(errors.given_name.message as string)
                                    }
                                    slotProps={{ 
                                        htmlInput: { maxLength: 60 },
                                        inputLabel: { shrink: isMobile ? true : undefined }
                                    }}                   
                                />
                            )}
                        />

                        <Controller
                            name="family_name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    required
                                    id={family_name_id}
                                    type="text"                                
                                    label={t("name.family_name.label")}
                                    error={!!errors.family_name}
                                    helperText={
                                        errors?.family_name?.message && t(errors.family_name.message as string)
                                    }
                                    slotProps={{ 
                                        htmlInput: { maxLength: 60 },
                                        inputLabel: { shrink: isMobile ? true : undefined }
                                    }}
                                />
                            )}
                        />
                        
                    </Stack>

                    <Stack direction={{ xs: "column", md: "row" }}  
                        sx={{ alignItems: {xs: "center", md: "flex-start"}, justifyContent: "flex-start", 
                        rowGap: 2, columnGap: 1, width: "100%" 
                    }}>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    required
                                    id={email_id}
                                    type="email"
                                    inputMode="email"
                                    label={t("email.label")}
                                    error={!!errors.email}
                                    helperText={
                                        errors?.email?.message && t(errors.email.message as string)
                                    }
                                    slotProps={{ 
                                        htmlInput: { maxLength: 255 },
                                        inputLabel: { shrink: isMobile ? true : undefined } 
                                    }}
                                    sx={{ width: {xs: "100%", md: "60%" } }}
                                />
                            )}
                        />

                        <Controller
                            name="phone_number"
                            control={control}
                            render={({ field }) => (
                                <MuiTelInput
                                    {...field}                                    
                                    required
                                    id={phone_number_id}
                                    label={t("phone_number.label")}
                                    error={!!errors.phone_number}
                                    helperText={
                                        errors?.phone_number?.message && t(errors.phone_number.message as string)
                                    }
                                    defaultCountry="US"
                                    slotProps={{ htmlInput: { maxLength: 20, autoComplete: "new-password" } }}
                                    sx={{ width: {xs: "100%", md: "40%" } }}
                                />
                            )}
                        />
                    </Stack>

                    {/* 🔑 ROW 3: NEW SUBMISSION & VALIDATION EXCEPTION AREA */}
                    <Box sx={{ 
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: {xs: "center", md: "flex-start"},
                        alignItems: "flex-start",
                        gap: 1,
                        width: "100%", 
                        mt: 1}}>
                        {/* 💥 Cross-Field Error Callout: Handles Zod refinement warnings */}
                        {segmentErrors?.departure_airport && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    display: "block", 
                                    color: "error.main"
                                }}
                            >
                                {t(segmentErrors.departure_airport.message as string)}
                            </Typography>
                        )}

                        {segmentErrors?.arrival_airport && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    display: "block", 
                                    color: "error.main"
                                }}
                            >
                                {t(segmentErrors.arrival_airport.message as string)}
                            </Typography>
                        )}

                        {segmentErrors?.return_date && (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    display: "block", 
                                    color: "error.main", 
                                    mb: 2
                                }}
                            >
                                {t(segmentErrors.return_date.message as string)}
                            </Typography>
                        )}

                        <Stack direction="row"
                            sx={{alignItems: {xs: "flex-end"}, gap: 2, justifyContent: "flex-end", width: "100%"}}>

                            <Button
                                type="button"
                                variant="text"
                                sx={{
                                    borderRadius: '5px',
                                    padding: "1.2em 2.2em",
                                    width: "max-content",
                                    minWidth: 0,
                                    flexShrink: 1
                                }}
                                onClick={() => reset()}
                                disabled={isLoading}
                            >
                                {t("flightReq.submission.reset")}
                            </Button>


                            {/* 🚀 Primary Search Trigger Button */}
                            <Button
                                variant="lightBg"
                                sx={{
                                    padding: "1.2em 2.2em",
                                    width: "max-content",
                                    minWidth: 0,
                                    flexShrink: 1
                                }}
                                type="submit"
                                disabled={isLoading}
                            >
                                {t("flightReq.submission.submit")}
                            </Button>
                        </Stack>
                    </Box>

                </Box>

                <CalendarPopover
                    departure_dates={dateRange} 
                    anchorEl={calendarAnchorEl}
                    open={openCalendarPopover}
                    flight={flight_type}                                               
                    onClose={handleCalendarPopoverClose}
                    >
                </CalendarPopover>

                <FlightTypePopover
                    allowedValues={Object.values(Flight).filter(v => v !== 'multiCity') as FlightValue[]}
                    anchorEl={flightAnchorEl}
                    open={openFlightTypePopover}
                    selectedValue={flight_type}                                                        
                    onClose={handleFlightTypePopoverClose}
                    >
                </FlightTypePopover>                     

                <PassengerPopover
                    allowedValues={Array.from({ length: 9 }, (_, i) => i + 1)}
                    anchorEl={passengerAnchorEl}
                    open={openPassengerPopover}
                    selectedValue={passenger_count}                                                        
                    onClose={handlePassengerPopoverClose}
                    >
                </PassengerPopover>

            </Box>

            <AirportsDialog
                selectedAirport={direction === Direction.FROM ? (departure_airport || undefined) : (arrival_airport || undefined)} 
                direction={direction} 
                open={dialogOpen} 
                onHandleDialogClose={handleDialogClose} 
                scroll={dialogScroll}
            />

             <Snackbar
				anchorOrigin={{ vertical, horizontal }}
				open={open}
				onClose={handleSnackbarClose}
				key={vertical + horizontal}
				autoHideDuration={null}
			>
				<SnackbarContent
					message={message}
					sx={{ backgroundColor: severity === "success" ? "var(--mui-palette-success)" : "var(--mui-palette-error)" }}
					action={
						<IconButton
							size="small"
							aria-label="close"
							color="inherit"
							onClick={handleSnackbarClose}
						>
							<CloseIcon fontSize="small" />
						</IconButton>
					}
				/>
			</Snackbar>

        </Box>
    );
};

export default FlightReqForm;
